import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Clock, CheckCircle, ChefHat, Package, Truck, XCircle, RefreshCw, User, Phone,
  Bell, BellOff, Undo2, Bike,
} from 'lucide-react';

const STATUS_CONFIG = {
  received: { label: 'Ricevuto', color: '#F59E0B', bg: '#FEF3C7', icon: Clock },
  confirmed: { label: 'Confermato', color: '#3B82F6', bg: '#DBEAFE', icon: CheckCircle },
  preparing: { label: 'In preparazione', color: '#8B5CF6', bg: '#EDE9FE', icon: ChefHat },
  ready: { label: 'Pronto', color: '#10B981', bg: '#D1FAE5', icon: Package },
  out_for_delivery: { label: 'In consegna', color: '#EC4899', bg: '#FCE7F3', icon: Bike },
  delivered: { label: 'Consegnato', color: '#64748B', bg: '#F1F5F9', icon: Truck },
  cancelled: { label: 'Annullato', color: '#EF4444', bg: '#FEE2E2', icon: XCircle },
};

const NEXT_STATUS = {
  received: 'confirmed',
  confirmed: 'preparing',
  preparing: 'ready',
  ready: 'delivered',
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [soundOn, setSoundOn] = useState(() => {
    const v = localStorage.getItem('admin_orders_sound');
    return v === null ? true : v === '1';
  });
  const knownIdsRef = useRef(null);
  const firstLoadRef = useRef(true);

  const playBeep = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // 2-tone urgent beep: 880Hz → 1320Hz
      const playTone = (freq, start, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
        gain.gain.setValueAtTime(0, ctx.currentTime + start);
        gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + start + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + start + duration);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + duration);
      };
      playTone(880, 0, 0.15);
      playTone(1320, 0.18, 0.25);
    } catch (e) { /* sound not available */ }
  }, []);

  const load = useCallback(async (silent = false) => {
    try {
      const [oRes, sRes] = await Promise.all([
        adminApi.get('/admin/orders'),
        adminApi.get('/admin/orders/stats'),
      ]);
      const newOrders = oRes.data.orders || [];
      setOrders(newOrders);
      setStats(sRes.data || {});

      // Detect new incoming orders (received/paid) to alert staff
      const activeIds = new Set(newOrders.filter(o => o.order_status === 'received' && o.payment_status === 'paid').map(o => o.id));
      if (firstLoadRef.current) {
        knownIdsRef.current = activeIds;
        firstLoadRef.current = false;
      } else {
        const previous = knownIdsRef.current || new Set();
        const newlyArrived = [...activeIds].filter(id => !previous.has(id));
        if (newlyArrived.length > 0 && !silent) {
          if (soundOn) playBeep();
          const newOrder = newOrders.find(o => o.id === newlyArrived[0]);
          toast.success(`🆕 Nuovo ordine #${newOrder?.order_number || ''}`, { duration: 8000 });
        }
        knownIdsRef.current = activeIds;
      }
    } catch { if (!silent) toast.error('Errore caricamento ordini'); }
    setLoading(false);
  }, [playBeep, soundOn]);

  useEffect(() => { load(); const iv = setInterval(() => load(false), 15000); return () => clearInterval(iv); }, [load]);

  useEffect(() => {
    adminApi.get('/admin/riders').then(r => setRiders((r.data || []).filter(x => x.active))).catch(() => {});
  }, []);

  const assignRider = async (order, riderId) => {
    if (!riderId) return;
    try {
      await adminApi.post(`/admin/orders/${order.id}/assign-rider`, { rider_id: riderId });
      toast.success('Rider assegnato');
      load(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const unassignRider = async (orderId) => {
    if (!window.confirm('Rimuovere il rider? L\'ordine tornerà in stato "Pronto".')) return;
    try {
      await adminApi.post(`/admin/orders/${orderId}/unassign-rider`);
      toast.success('Rider rimosso');
      load(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem('admin_orders_sound', next ? '1' : '0');
    if (next) playBeep();
    toast.success(next ? 'Notifiche sonore attive' : 'Notifiche sonore disattivate');
  };

  const advanceStatus = async (orderId, newStatus) => {
    try {
      await adminApi.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Stato aggiornato: ${STATUS_CONFIG[newStatus]?.label}`);
      load(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Annullare questo ordine? Il cliente NON verrà rimborsato.')) return;
    try {
      await adminApi.put(`/admin/orders/${orderId}/status`, { status: 'cancelled' });
      toast.success('Ordine annullato');
      load(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const refundOrder = async (order) => {
    const method = order.payment_method === 'stripe' ? 'Stripe' : order.payment_method === 'paypal' ? 'PayPal' : 'punti';
    if (!window.confirm(`Rimborsare ${order.total.toFixed(2)}€ via ${method}?\n\nL'ordine sarà cancellato e il cliente rimborsato. I punti guadagnati saranno stornati.`)) return;
    try {
      const r = await adminApi.post(`/admin/orders/${order.id}/refund`, { reason: 'Rimborso richiesto dall\'admin' });
      toast.success(r.data.message || 'Rimborso effettuato');
      load(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore rimborso'); }
  };

  const filtered = orders.filter(o => {
    if (filter === 'active') return !['delivered', 'cancelled'].includes(o.order_status);
    if (filter === 'completed') return o.order_status === 'delivered';
    if (filter === 'cancelled') return o.order_status === 'cancelled';
    return true;
  });

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-64">
      <div className="w-8 h-8 border-3 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1A1A1A]">Ordini</h1>
          <p className="text-[#64748B] text-base mt-1">Gestisci gli ordini take-away in tempo reale</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleSound}
            data-testid="toggle-sound"
            title={soundOn ? 'Disattiva notifiche sonore' : 'Attiva notifiche sonore'}
            className={`p-2 rounded-lg transition-colors ${soundOn ? 'bg-[#FDECE7] text-[#D44E20]' : 'hover:bg-slate-100 text-slate-400'}`}
          >
            {soundOn ? <Bell size={18} /> : <BellOff size={18} />}
          </button>
          <button onClick={() => load(true)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors" data-testid="refresh-orders">
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Oggi', value: stats.total_today || 0, color: '#1A1A1A' },
          { label: 'In attesa', value: stats.pending || 0, color: '#F59E0B' },
          { label: 'In preparazione', value: stats.preparing || 0, color: '#8B5CF6' },
          { label: 'Pronti', value: stats.ready || 0, color: '#10B981' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
            <div className="text-xs text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'active', label: 'Attivi' },
          { key: 'completed', label: 'Completati' },
          { key: 'cancelled', label: 'Annullati' },
          { key: 'all', label: 'Tutti' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              filter === f.key ? 'bg-[#D44E20] text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-700'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Orders */}
      {filtered.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <Package size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">Nessun ordine {filter === 'active' ? 'attivo' : ''}</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(order => {
          const sc = STATUS_CONFIG[order.order_status] || STATUS_CONFIG.received;
          const StatusIcon = sc.icon;
          const nextStatus = NEXT_STATUS[order.order_status];
          const nextSc = nextStatus ? STATUS_CONFIG[nextStatus] : null;
          const time = order.created_at ? new Date(order.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';

          return (
            <div key={order.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`order-${order.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: sc.bg }}>
                    <StatusIcon size={18} style={{ color: sc.color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[#1A1A1A]">#{order.order_number}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                      {order.order_type === 'delivery' ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-pink-50 text-pink-700" data-testid={`type-badge-${order.id}`}>
                          🛵 DELIVERY
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-orange-50 text-orange-700" data-testid={`type-badge-${order.id}`}>
                          🥡 TAKEAWAY
                        </span>
                      )}
                      {order.payment_method === 'cod' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-800">
                          💶 CONTRASSEGNO
                        </span>
                      )}
                      <span className="text-xs text-slate-400">{time}</span>
                      {order.eta_at && !['delivered', 'cancelled', 'ready'].includes(order.order_status) && (
                        <span data-testid={`order-eta-${order.id}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${order.overdue ? 'bg-red-100 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}
                          title={`Pronto stimato entro le ${order.eta_at ? new Date(order.eta_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : ''}`}>
                          ⏱ {order.overdue ? 'In ritardo' : (order.minutes_remaining <= 0 ? 'Pronto ora' : `~${order.minutes_remaining} min`)}
                        </span>
                      )}
                    </div>
                    {order.customer_name && (
                      <div className="flex items-center gap-2 mt-0.5">
                        <User size={12} className="text-slate-400" />
                        <span className="text-xs text-slate-500">{order.customer_name}</span>
                        {order.customer_phone && <>
                          <Phone size={12} className="text-slate-400" />
                          <span className="text-xs text-slate-500">{order.customer_phone}</span>
                        </>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-[#D44E20]">{order.total?.toFixed(2)} EUR</div>
                  <div className="text-[10px] text-slate-400">
                    {order.payment_method === 'points' ? `${order.points_used} punti` : 'Stripe'}
                    {order.payment_status === 'paid' ? ' - Pagato' : ' - In attesa'}
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="bg-slate-50 rounded-lg p-3 mb-3">
                {order.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm py-0.5">
                    <span className="text-slate-700">{item.quantity}x {item.name}</span>
                    <span className="text-slate-500">{item.subtotal?.toFixed(2)} EUR</span>
                  </div>
                ))}
              </div>

              {order.notes && <p className="text-xs text-slate-500 italic mb-3">Note: {order.notes}</p>}
              {order.pickup_time && order.order_type !== 'delivery' && <p className="text-xs text-slate-500 mb-3">Ritiro: {order.pickup_time}</p>}

              {/* Delivery address block */}
              {order.order_type === 'delivery' && order.delivery_address && (
                <div className="bg-pink-50 border border-pink-100 rounded-lg p-3 mb-3 text-xs" data-testid={`delivery-block-${order.id}`}>
                  <div className="flex items-start gap-2">
                    <Truck size={14} className="mt-0.5 text-pink-600 flex-shrink-0"/>
                    <div className="flex-1">
                      <p className="font-semibold text-[#1A1A1A]">{order.delivery_address.street}, {order.delivery_address.zip} {order.delivery_address.city}</p>
                      {order.delivery_address.intercom && <p className="text-slate-600">🔔 Citofono: <b>{order.delivery_address.intercom}</b></p>}
                      {order.delivery_address.phone && <p className="text-slate-600">📞 {order.delivery_address.phone}</p>}
                      {order.delivery_address.notes && <p className="text-amber-700 mt-0.5">📝 {order.delivery_address.notes}</p>}
                      {order.delivery_info?.zone_label && <p className="text-slate-500 mt-0.5 text-[10px]">Zona: {order.delivery_info.zone_label} · ETA {order.delivery_info.eta_min}' · Fee {order.delivery_fee?.toFixed(2)}€</p>}
                    </div>
                  </div>
                  {/* Rider assignment */}
                  {order.payment_status === 'paid' || order.payment_status === 'pending_cod' ? (
                    order.rider_id ? (
                      <div className="mt-2 flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-pink-200" data-testid={`rider-assigned-${order.id}`}>
                        <div className="flex items-center gap-2 text-xs">
                          <Bike size={14} className="text-pink-600"/>
                          <span className="font-semibold text-[#1A1A1A]">Rider: {order.rider_name || '(?)'}</span>
                          {order.rider_phone && <span className="text-slate-500">· {order.rider_phone}</span>}
                        </div>
                        {order.order_status !== 'delivered' && (
                          <button onClick={() => unassignRider(order.id)} data-testid={`unassign-rider-${order.id}`}
                            className="text-[11px] text-red-500 hover:underline">
                            Rimuovi
                          </button>
                        )}
                      </div>
                    ) : order.order_status === 'ready' || order.order_status === 'out_for_delivery' ? (
                      <div className="mt-2 flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-pink-200">
                        <Bike size={14} className="text-pink-600"/>
                        <span className="text-xs font-semibold text-[#1A1A1A]">Assegna rider:</span>
                        <select onChange={e => { assignRider(order, e.target.value); e.target.value = ''; }} defaultValue=""
                          data-testid={`assign-rider-select-${order.id}`}
                          className="flex-1 h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs">
                          <option value="">Seleziona rider…</option>
                          {riders.map(r => <option key={r.id} value={r.id}>{r.name} ({r.phone})</option>)}
                        </select>
                      </div>
                    ) : null
                  ) : null}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                {nextSc && order.payment_status === 'paid' && (
                  <button onClick={() => advanceStatus(order.id, nextStatus)} data-testid={`advance-${order.id}`}
                    className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors"
                    style={{ backgroundColor: nextSc.color }}>
                    {nextSc.label}
                  </button>
                )}
                {!['delivered', 'cancelled'].includes(order.order_status) && (
                  <button onClick={() => cancelOrder(order.id)} data-testid={`cancel-${order.id}`}
                    className="px-4 py-2 text-red-500 text-sm font-semibold rounded-lg border border-red-200 hover:bg-red-50 transition-colors">
                    Annulla
                  </button>
                )}
                {order.payment_status === 'paid' && !order.refunded_at && (
                  <button onClick={() => refundOrder(order)} data-testid={`refund-${order.id}`}
                    className="flex items-center gap-2 px-4 py-2 text-amber-700 text-sm font-semibold rounded-lg border border-amber-300 hover:bg-amber-50 transition-colors">
                    <Undo2 size={14} /> Rimborsa
                  </button>
                )}
                {order.refunded_at && (
                  <span data-testid={`refunded-badge-${order.id}`}
                    className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-amber-100 text-amber-800 border border-amber-300">
                    <Undo2 size={12} /> Rimborsato
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
